import React from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    CircularProgress, Typography, Button, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, TextField, Dialog, DialogTitle, 
    DialogContent, DialogActions, Checkbox, FormControlLabel, MenuItem, Select, FormControl, InputLabel
} from "@material-ui/core";
import {ServiceLocator} from "../../../Services/ServiceLocator";

import {faArrowLeft, faEdit, faSave, faTimes, faPlus, faCheck, faTrash, faInfoCircle, faEye, faPeopleCarry} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import dayjs from 'dayjs';
import axios from 'axios';
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import {Autocomplete} from '@material-ui/lab';
import PackingSlipDetails from './PackingSlipDetails';

import { AuthenticationService } from "../../../Services/AuthenticationService";
import { getNumberInputDisplayValue, parseNumberInputValue } from "../../../utils/numberInputUtils";
import PartAutocompleteOption from '../../Common/PartAutocompleteOption';
import PartDisplay from '../../Common/PartDisplay';
import SortableTableHeader from '../../Common/SortableTableHeader';
import { bindTableSortHandler, sortCollection, RECEIVING_ACTIONS_SORT_ACCESSORS } from '../../../utils/tableSortUtils';
import { isPackingSlipFullyReceived } from '../../../utils/packingSlipShipmentUtils';
const { v4: uuid } = require('uuid');
const styles = theme => ({
    wrapper: {
        margin: 0,
        padding: 0
    },
    headerSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 30,
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    poNumber: {
        fontSize: '2.5rem',
        fontWeight: 'bold',
        color: '#2196f3',
        margin: 0
    },
    backButton: {
        marginBottom: 20
    },
    sectionContainer: {
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: 20
    },
    sectionTitle: {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        marginBottom: '20px',
        marginTop: 0,
        color: '#333'
    }
});

let editingOrder;
let globalProps;

class EditPurchaseOrder extends React.Component {
    state = {
        loading: true,
        user: {},
        permissions: [],
        adminUsers: [],
        vendors: [],
        parts: [],
        invoices: [],
        editingVendorOrderNumber: '',
        loadingPackingSlips: false,
        savingPurchaseOrder: false,
        showCopyTable: false,
        copyTableData: [],
        
        // Invoice management state
        createLineItemInvoiceOpen: false,
        attachExistingInvoiceToLineItemsOpen: false,
        selectedLineItems: {},
        lastSelectedLineItemIndex: null,
        newInvoiceNumber: '',
        newInvoiceFile: null,
        selectedExistingInvoiceId: '',
        savingInvoice: false,
        purchasedPartInvoices: {},
        
        // Packing slip management state
        packingSlips: [],
        selectedPackingSlip: null,
        receivingPackingSlip: false,
        packingSlipDetailsOpen: false,
        selectedPackingSlipId: null,
        
        // Receiving actions state
        receivingActions: [],
        loadingReceivingActions: false,
        receivingActionsSortField: 'receivedDate',
        receivingActionsSortDirection: 'desc',
        receiveDrawerOpen: false,
        receivedQuantities: {},

        // Invoice state
        
        // Purchase order line items editing state
        editingLineItems: false,
        editedQuantities: {},
        editedUnitCosts: {},
        editedTotalPrices: {},
        editedCompanyNames: {},
        pendingNewLineItems: [],
        addingNewLineItem: false,
        
        // Line items drawer state
        lineItemsDrawerOpen: false,
        selectedVendor: null,
        availableVendors: [],
        partsOptions: [],
        partFamilies: [],
        purchaseOrderVendor: null,
        purchaseOrderVendorId: null,
        updatingPaidStatus: false,
        paidStatusAction: null,
    };

    constructor(props) {
        super(props);
        globalProps = props;

        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        // Update document title when purchase order data is loaded
        if (editingOrder && editingOrder.purchaseOrderNumber && (!prevState.loading || (prevState.loading && !this.state.loading))) {
            document.title = `PO# ${editingOrder.purchaseOrderNumber}`;
        }
    }

    loadData = async () => {
        const self = this;
        const user = AuthenticationService.getInstance().getUser();

        try {
            // Load all required data
            await Promise.all([
                self.loadAdminUsers(),
                self.loadVendors(),
                self.loadParts(),
                self.loadPartFamilies(),
                self.loadInvoices()
            ]);

            // Load the specific purchase order
            const purchaseOrderId = globalProps.match.params.purchaseOrderId;
            const partService = ServiceLocator.Instance.getPartService();
            const allPurchaseOrders = await partService.getPurchaseOrders();
            const purchaseOrder = allPurchaseOrders.find(po => po.id === purchaseOrderId);
            
            if (!purchaseOrder) {
                globalProps.history.push('/admin/purchases');
                return;
            }

            // Load packing slips for this order
            self.setState({ loadingPackingSlips: true });
            const packingSlips = await partService.getPackingSlipsByPurchaseOrderId(purchaseOrder.id);
            
            editingOrder = {
                ...purchaseOrder,
                packingSlips: packingSlips || []
            };

            // Load all purchased part invoices
            await self.loadAllPurchasedPartInvoices();

            // Recalculate tiered costs for existing line items
            self.recalculateTieredCostsForExistingItems();
            
            // Load receiving actions for all purchased parts
            await self.loadReceivingActionsForPurchaseOrder(purchaseOrder.id);

            self.setState({
                loading: false, 
                user, 
                permissions: user.userPermissions,
                editingVendorOrderNumber: purchaseOrder.vendorOrderNumber || '',
                loadingPackingSlips: false
            });
            
            // Update document title
            if (editingOrder && editingOrder.purchaseOrderNumber) {
                document.title = `PO# ${editingOrder.purchaseOrderNumber}`;
            }
        } catch (error) {
            console.error('Error loading purchase order:', error);
            globalProps.history.push('/admin/purchases');
        }
    };

    loadAdminUsers = () => {
        const userService = ServiceLocator.Instance.getUserService();
        return userService.getAdminUsers().then((adminUsers) => {
            this.setState({adminUsers});
        });
    };

    loadVendors = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllVendors().then((vendors) => {
            this.setState({vendors});
        });
    };

    loadParts = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllPartsExpanded().then((parts) => {
            this.setState({parts});
        });
    };

    loadPartFamilies = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getPartFamilies().then((partFamilies) => {
            this.setState({partFamilies});
        });
    };

    loadInvoices = () => {
        const partService = ServiceLocator.Instance.getPartService();
        return partService.getAllInvoices().then((invoices) => {
            this.setState({invoices});
        });
    };

    loadAllPurchasedPartInvoices = async () => {
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const purchasedPartIds = editingOrder.purchasedParts.map(part => part.id);
            const purchasedPartInvoices = {};
            
            for (const purchasedPartId of purchasedPartIds) {
                const invoices = await partService.getInvoicesForPurchasedPart(purchasedPartId);
                purchasedPartInvoices[purchasedPartId] = invoices;
            }
            
            this.setState({ purchasedPartInvoices });
        } catch (error) {
            console.error('Error loading purchased part invoices:', error);
        }
    };

    recalculateTieredCostsForExistingItems = () => {
        if (!editingOrder || !editingOrder.purchasedParts) return;

        editingOrder.purchasedParts.forEach(part => {
            const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
            if (!partExpanded) return;

            const vendorPart = partExpanded.part.vendorParts.find(vp => vp.vendorId === part.vendorId);
            if (!vendorPart) return;

            // Only recalculate if using tiered pricing and not manually overridden
            if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0 && !part.isOverridden) {
                const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                
                // Find the appropriate tier for the current quantity
                const tier = part.quantity < sortedTiers[0].quantity 
                    ? sortedTiers[0] 
                    : [...sortedTiers].reverse().find(t => part.quantity >= t.quantity) || sortedTiers[sortedTiers.length - 1];
                
                if (tier) {
                    part.price = Number(tier.price).toFixed(3);
                    part.pricePaid = Number(tier.price).toFixed(3);
                    part.total = Number(tier.price * part.quantity).toFixed(3);
                }
            }
        });
    };

    handlePurchaseOrderClick = (event) => {
        const purchaseOrderId = editingOrder.id;
        
        if (event.ctrlKey) {
            // Open in new tab
            window.open(`/admin/purchases/edit/${purchaseOrderId}`, '_blank');
        } else {
            // Stay on current page (do nothing, as we're already on the edit page)
            // Or optionally navigate back to purchases list
            // globalProps.history.push('/admin/purchases');
        }
    };

    handleBack = () => {
        globalProps.history.push('/admin/purchases');
    };

    deletePurchaseOrder = async () => {
        const confirm = window.confirm("Are you sure you want to delete this entire purchase order? This will delete all line items associated with this purchase order.");
        if (!confirm) return;

        const self = this;
        self.setState({ savingPurchaseOrder: true });
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const purchasedPartIds = editingOrder.purchasedParts.map(part => part.id);
            
            // Delete all purchased parts for this purchase order
            const deletePromises = purchasedPartIds.map(partId => {
                return partService.deletePurchaseLog(partId);
            });
            
            await Promise.all(deletePromises);
            
            window.$alertSuccess("Purchase order deleted successfully");
            globalProps.history.push('/admin/purchases');
        } catch (error) {
            console.error('Error deleting purchase order:', error);
            window.$alertError('Error deleting purchase order');
            self.setState({ savingPurchaseOrder: false });
        }
    };

    handleSave = () => {
        this.setState({ savingPurchaseOrder: true });
        const partService = ServiceLocator.Instance.getPartService();
        
        const payload = {
            ...editingOrder,
            vendorOrderNumber: this.state.editingVendorOrderNumber
        };

        partService.updatePurchaseOrder(payload).then(() => {
            window.$alertSuccess("Purchase order updated successfully");
            this.setState({ savingPurchaseOrder: false });
        }).catch((error) => {
            window.$alertError("Error updating purchase order");
            console.error(error);
            this.setState({ savingPurchaseOrder: false });
        });
    };

    // Invoice Management Methods
    handleCreateLineItemInvoice = (purchaseOrderId) => {
        this.setState({
            createLineItemInvoiceOpen: true,
            selectedLineItems: {},
            lastSelectedLineItemIndex: null,
            newInvoiceNumber: '',
            newInvoiceFile: null
        });
    };

    handleAttachExistingLineItemInvoice = (purchaseOrderId) => {
        this.setState({
            attachExistingInvoiceToLineItemsOpen: true,
            selectedLineItems: {},
            lastSelectedLineItemIndex: null,
            selectedExistingInvoiceId: ''
        });
    };

    handleLineItemSelection = (purchasedPartId, isSelected, event) => {
        const visibleLineItems = this.getVisibleLineItems();
        const currentIndex = visibleLineItems.findIndex(part => part.id === purchasedPartId);
        const isShiftPressed = !!(event?.shiftKey || event?.nativeEvent?.shiftKey);

        if (isShiftPressed && this.state.lastSelectedLineItemIndex !== null && currentIndex !== -1) {
            const startIndex = Math.min(this.state.lastSelectedLineItemIndex, currentIndex);
            const endIndex = Math.max(this.state.lastSelectedLineItemIndex, currentIndex);
            const rangeIds = visibleLineItems.slice(startIndex, endIndex + 1).map(part => part.id);

            this.setState(prevState => {
                const selectedLineItems = { ...prevState.selectedLineItems };
                const anchorId = visibleLineItems[prevState.lastSelectedLineItemIndex]?.id;
                const anchorIsSelected = anchorId ? !!selectedLineItems[anchorId] : true;
                const allInRangeSelected = rangeIds.every(id => !!selectedLineItems[id]);
                const shouldDeselectRange = !anchorIsSelected || (isSelected && allInRangeSelected);

                if (shouldDeselectRange) {
                    rangeIds.forEach(id => {
                        selectedLineItems[id] = false;
                    });
                } else {
                    rangeIds.forEach(id => {
                        selectedLineItems[id] = true;
                    });
                }

                return {
                    selectedLineItems,
                    lastSelectedLineItemIndex: currentIndex
                };
            });
            return;
        }

        this.setState(prevState => ({
            selectedLineItems: {
                ...prevState.selectedLineItems,
                [purchasedPartId]: isSelected
            },
            lastSelectedLineItemIndex: currentIndex !== -1 ? currentIndex : prevState.lastSelectedLineItemIndex
        }));
    };

    handleSelectAllLineItems = () => {
        const allSelected = {};
        editingOrder.purchasedParts
            .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
            .forEach(part => {
                allSelected[part.id] = true;
            });
        this.setState({ selectedLineItems: allSelected, lastSelectedLineItemIndex: null });
    };

    handleSelectNoneLineItems = () => {
        this.setState({ selectedLineItems: {}, lastSelectedLineItemIndex: null });
    };

    handleCloseCreateLineItemInvoice = () => {
        this.setState({
            createLineItemInvoiceOpen: false,
            selectedLineItems: {},
            lastSelectedLineItemIndex: null,
            newInvoiceNumber: '',
            newInvoiceFile: null
        });
    };

    handleCloseAttachExistingInvoiceToLineItems = () => {
        this.setState({
            attachExistingInvoiceToLineItemsOpen: false,
            selectedLineItems: {},
            lastSelectedLineItemIndex: null,
            selectedExistingInvoiceId: ''
        });
    };

    handleSaveCreateLineItemInvoice = async () => {
        if (!this.state.newInvoiceNumber.trim()) {
            window.$alertError('Invoice number is required');
            return;
        }

        const selectedLineItems = Object.keys(this.state.selectedLineItems).filter(id => this.state.selectedLineItems[id]);
        if (selectedLineItems.length === 0) {
            window.$alertError('Please select at least one line item');
            return;
        }

        this.setState({savingInvoice: true});
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const invoice = {
                id: uuid(),
                invoiceNumber: this.state.newInvoiceNumber,
                isPaid: false,
                userId: AuthenticationService.getInstance().getUserId()
            };


            await partService.createInvoice(invoice, this.state.newInvoiceFile);
            
            // Attach to selected line items if any
            if (selectedLineItems.length > 0) {
                // Create quantities object for bulk attachment
                const quantitiesForAttachment = {};
                selectedLineItems.forEach(purchasedPartId => {
                    quantitiesForAttachment[purchasedPartId] = 1; // Default quantity of 1
                });
                
                // Use bulk attachment with quantities
                await partService.bulkAttachInvoiceToPurchasedParts(invoice.id, quantitiesForAttachment);
            }
            
            window.$alertSuccess('Invoice created and attached to selected line items successfully');
            this.handleCloseCreateLineItemInvoice();
            await this.loadData();
        } catch (error) {
            console.error('Error creating invoice:', error);
            window.$alertError('Failed to create invoice');
        } finally {
            this.setState({savingInvoice: false});
        }
    };

    handleSaveAttachExistingInvoiceToLineItems = async () => {
        if (!this.state.selectedExistingInvoiceId) {
            window.$alertError('Please select an invoice to attach');
            return;
        }

        const selectedLineItems = Object.keys(this.state.selectedLineItems).filter(id => this.state.selectedLineItems[id]);
        if (selectedLineItems.length === 0) {
            window.$alertError('Please select at least one line item');
            return;
        }

        this.setState({savingInvoice: true});
        
        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Attach the invoice to each selected line item
            for (const purchasedPartId of selectedLineItems) {
                await partService.attachInvoiceToPurchasedPart(purchasedPartId, this.state.selectedExistingInvoiceId);
            }
            
            window.$alertSuccess('Invoice attached to selected line items successfully');
            this.handleCloseAttachExistingInvoiceToLineItems();
            await this.loadData();
        } catch (error) {
            console.error('Error attaching invoice to line items:', error);
            window.$alertError('Failed to attach invoice');
        } finally {
            this.setState({savingInvoice: false});
        }
    };

    handleViewInvoice = (invoice, event) => {
        const path = `/admin/invoices/${invoice.id}`;
        if (event && (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1)) {
            window.open(path, '_blank');
            return;
        }
        window.location.assign(path);
    };

    handleToggleInvoicePaidStatus = async (invoiceId, currentStatus) => {
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.toggleInvoicePaidStatus(invoiceId, !currentStatus);
            
            window.$alertSuccess(`Invoice marked as ${!currentStatus ? 'paid' : 'unpaid'}`);
            await this.loadData();
        } catch (error) {
            console.error('Error toggling invoice paid status:', error);
            window.$alertError('Failed to update invoice status');
        }
    };

    // Helper function to extract box number from packing slip name
    extractBoxNumber = (packingSlipName) => {
        if (!packingSlipName) return null;
        
        // Try to match patterns like " - Box 9" or " Box 9" or "- Box 9"
        const boxMatch = packingSlipName.match(/\s*-\s*Box\s+(\d+)/i) || 
                        packingSlipName.match(/\s+Box\s+(\d+)/i) ||
                        packingSlipName.match(/Box\s+(\d+)/i);
        
        if (boxMatch && boxMatch[1]) {
            return `Box ${boxMatch[1]}`;
        }
        
        return null;
    };

    // Helper function to remove box number from packing slip name
    removeBoxNumberFromName = (packingSlipName) => {
        if (!packingSlipName) return packingSlipName;
        
        // Remove patterns like " - Box 9" or " Box 9" or "- Box 9"
        return packingSlipName
            .replace(/\s*-\s*Box\s+\d+/i, '')
            .replace(/\s+Box\s+\d+/i, '')
            .replace(/Box\s+\d+/i, '')
            .trim();
    };

    handleReceivingActionsSort = bindTableSortHandler(this, {
        fieldKey: 'receivingActionsSortField',
        directionKey: 'receivingActionsSortDirection',
    });

    getSortedReceivingActions = () => {
        return sortCollection(
            this.state.receivingActions,
            this.state.receivingActionsSortField,
            this.state.receivingActionsSortDirection,
            RECEIVING_ACTIONS_SORT_ACCESSORS
        );
    };

    loadReceivingActionsForPurchaseOrder = async (purchaseOrderId) => {
        this.setState({ loadingReceivingActions: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();

            if (!editingOrder || !editingOrder.purchasedParts) {
                this.setState({ receivingActions: [], loadingReceivingActions: false });
                return;
            }

            const actions = await partService.getReceivingActionsByPurchaseOrderId(purchaseOrderId);
            const combinedReceivingActions = (actions || []).map(action => ({
                ...action,
                packingSlip: action.packingSlipId ? {
                    id: action.packingSlipId,
                    name: action.packingSlipName,
                    fileUrl: action.packingSlipFileUrl,
                    fileName: action.packingSlipFileName || action.packingSlipName || 'packing-slip-file'
                } : null
            }));

            this.setState({
                receivingActions: combinedReceivingActions,
                loadingReceivingActions: false
            });
        } catch (error) {
            console.error('Error loading receiving actions for purchase order:', error);
            this.setState({
                receivingActions: [],
                loadingReceivingActions: false
            });
        }
    };

    isDirectReceiveAction = (action) => {
        return !action.packingSlipId && !action.packingSlip;
    };

    handleDeleteDirectReceiveAction = async (action) => {
        if (!this.isDirectReceiveAction(action)) {
            return;
        }

        const quantity = action.quantityReceived || 0;
        if (!window.confirm(`Delete this direct receive (${quantity} qty)? This will reduce the received quantity on this line item and cannot be undone.`)) {
            return;
        }

        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.deleteReceivingAction(action.id);
            await this.loadData();
            if (editingOrder?.id) {
                await this.loadReceivingActionsForPurchaseOrder(editingOrder.id);
            }
            window.$alertSuccess('Direct receive deleted');
        } catch (error) {
            console.error('Error deleting direct receive:', error);
            window.$alertError('Failed to delete direct receive');
        }
    };

    // Packing Slip Management Methods
    handlePackingSlipSelect = (packingSlip) => {
        this.setState({
            packingSlipDetailsOpen: true,
            selectedPackingSlipId: packingSlip.id
        });
    };

    handleClosePackingSlipDetails = () => {
        this.setState({
            packingSlipDetailsOpen: false,
            selectedPackingSlipId: null
        });
    };

    handlePackingSlipDetailsUpdate = () => {
        // Refresh the data when receiving actions are updated
        this.loadData();
    };

    handleReceivePackingSlip = async (packingSlipId) => {
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.receivePackingSlip(packingSlipId, AuthenticationService.getInstance().getUserId());
            
                         // Refresh the purchase order data
             await this.loadData();
            window.$alertSuccess('Packing slip received successfully');
        } catch (error) {
            console.error('Error receiving packing slip:', error);
            window.$alertError('Error receiving packing slip');
        }
    };

    handleQuantityChange = (itemId, value) => {
        this.setState(prevState => ({
            receivedQuantities: {
                ...prevState.receivedQuantities,
                [itemId]: parseNumberInputValue(value)
            }
        }));
    };

    closeReceiveDrawer = () => {
        this.setState({
            receiveDrawerOpen: false,
            selectedPackingSlip: null,
            receivedQuantities: {}
        });
    };

    // Line item editing methods
    handleEditLineItems = async () => {
        const editedQuantities = {};
        editingOrder.purchasedParts.forEach(part => {
            editedQuantities[part.id] = part.quantity;
        });
        
        // Determine the PO vendor once and keep comparisons normalized.
        const normalizeVendorName = (name) => (name || '').trim().toLowerCase();
        const firstVendorName = editingOrder.purchasedParts.length > 0
            ? editingOrder.purchasedParts[0].companyName
            : null;
        const purchaseOrderVendorByName = this.state.vendors.find(v =>
            normalizeVendorName(v.name) === normalizeVendorName(firstVendorName)
        );
        const purchaseOrderVendorId = purchaseOrderVendorByName?.id
            || editingOrder.purchasedParts.find(part => part.vendorId)?.vendorId
            || null;
        const purchaseOrderVendor = purchaseOrderVendorByName?.name || firstVendorName || null;
        
        // Load the data needed for the interface
        try {
            const partService = ServiceLocator.Instance.getPartService();
            
            // Load part families
            const partFamilies = await partService.getPartFamilies();
            
            // Create parts options (combining families and parts, filtered by vendor)
            const partsOptions = [];
            
            // Add part families first (only if they contain parts available from the PO vendor)
            partFamilies
                .filter(family => {
                    // Only include families that have at least one part available from the purchase order vendor
                    if (!purchaseOrderVendor) return true; // If no vendor determined, show all
                    
                    // Check if any part in this family is available from the PO vendor
                    return this.state.parts.some(partData => 
                        partData.part.partToFamilies?.some(ptf => ptf.familyId === family.id && !ptf.isImpliedPart) &&
                        partData.part.isActive && 
                        partData.part.currentlyPurchasing &&
                        partData.part.vendorParts?.some(vp => {
                            if (purchaseOrderVendorId) return vp.vendorId === purchaseOrderVendorId;
                            const vendor = this.state.vendors.find(v => v.id === vp.vendorId);
                            return normalizeVendorName(vendor?.name) === normalizeVendorName(purchaseOrderVendor);
                        })
                    );
                })
                .forEach((family) => {
                    partsOptions.push({
                        title: `${family.partFamilyId} (Family)`,
                        value: family.id,
                        isFamily: true,
                        family: family
                    });
                });
            
            // Add individual parts (only currently purchasing ones that have the PO vendor)
            this.state.parts
                .filter(x => x.part.isActive && x.part.currentlyPurchasing)
                .filter(partData => {
                    // Only include parts that have vendor parts for this purchase order's vendor
                    if (!purchaseOrderVendor) return true; // If no vendor determined, show all
                    return partData.part.vendorParts?.some(vp => {
                        if (purchaseOrderVendorId) return vp.vendorId === purchaseOrderVendorId;
                        const vendor = this.state.vendors.find(v => v.id === vp.vendorId);
                        return normalizeVendorName(vendor?.name) === normalizeVendorName(purchaseOrderVendor);
                    });
                })
                .forEach((partData) => {
                    partsOptions.push({
                        title: `${partData.part.partId} rev ${partData.part.revision}`,
                        value: partData.part.id,
                        isFamily: false,
                        part: partData.part
                    });
                });
            
            this.setState({ 
                lineItemsDrawerOpen: true,
                editedQuantities,
                partFamilies,
                partsOptions,
                purchaseOrderVendor,
                purchaseOrderVendorId,
                editingLineItems: true
            });
        } catch (error) {
            console.error('Error loading data for line item editing:', error);
            window.$alertError('Error loading data for editing');
        }
    };

    handleCloseLineItemsDrawer = () => {
        this.setState({
            lineItemsDrawerOpen: false,
            editingLineItems: false,
            editedQuantities: {},
            editedCompanyNames: {},
            pendingNewLineItems: [],
            selectedVendor: null,
            availableVendors: [],
            purchaseOrderVendorId: null
        });
    };

    handleCancelEditLineItems = () => {
        this.setState({
            editingLineItems: false,
            editedQuantities: {},
            editedCompanyNames: {},
            addingNewLineItem: false,
            pendingNewLineItems: []
        });
    };

    handleSaveLineItems = async () => {
        const self = this;
        self.setState({savingPurchaseOrder: true});

        try {
            const partService = ServiceLocator.Instance.getPartService();
            const user = AuthenticationService.getInstance().getUser();

            // Save changes to existing items (exclude deleted items)
            const updatePromises = Object.keys(this.state.editedQuantities)
                .filter(partId => !(this.state.deletedLineItems || []).includes(partId))
                .map(partId => {
                const originalPart = editingOrder.purchasedParts.find(p => p.id === partId);
                const newQuantity = this.state.editedQuantities[partId];
                const newTotalPrice = this.state.editedTotalPrices[partId];
                const newCompanyName = this.state.editedCompanyNames[partId];
                
                if (originalPart && (originalPart.quantity !== newQuantity || originalPart.pricePaid !== newTotalPrice || (newCompanyName && originalPart.companyName !== newCompanyName))) {
                    const payload = {
                        ...originalPart,
                        quantity: parseNumberInputValue(newQuantity) || originalPart.quantity,
                        pricePaid: newTotalPrice || originalPart.pricePaid,
                        companyName: newCompanyName || originalPart.companyName
                    };
                    return partService.updatePurchaseLog(payload);
                }
                return Promise.resolve();
            });

            // Save new line items
            const newItemPromises = this.state.pendingNewLineItems.map(newItem => {
                if (newItem.partId && newItem.quantity > 0) {
                    const fallbackVendorName = this.state.purchaseOrderVendor
                        || editingOrder.purchasedParts?.[0]?.companyName
                        || '';
                    const payload = {
                        id: uuid(),
                        partId: newItem.partId,
                        quantity: parseNumberInputValue(newItem.quantity),
                        pricePaid: parseNumberInputValue(newItem.pricePaid) * parseNumberInputValue(newItem.quantity) || 0,
                        companyName: newItem.vendorName || fallbackVendorName,
                        vendorId: newItem.vendorId || this.state.purchaseOrderVendorId || null,
                        orderId: editingOrder.id,
                        orderDate: new Date(),
                        userId: user.id
                    };
                    return partService.createPurchaseLog(payload);
                }
                return Promise.resolve();
            });

            // Delete marked items
            const deletePromises = (this.state.deletedLineItems || []).map(partId => {
                return partService.deletePurchaseLog(partId);
            });

            await Promise.all([...updatePromises, ...newItemPromises, ...deletePromises]);
            await self.loadData(); // Reload to get updated data

            self.setState({
                lineItemsDrawerOpen: false,
                editingLineItems: false,
                editedQuantities: {},
                editedUnitCosts: {},
                editedTotalPrices: {},
                pendingNewLineItems: [],
                deletedLineItems: [],
                selectedVendor: null,
                availableVendors: [],
                savingPurchaseOrder: false
            });
            window.$alertSuccess('Purchase order updated successfully');
        } catch (error) {
            console.error('Error saving purchase order:', error);
            window.$alertError('Error saving purchase order');
            self.setState({savingPurchaseOrder: false});
        }
    };

    handleLineItemQuantityChange = (partId, quantity) => {
        // Allow empty string for free editing
        if (quantity === '') {
            this.setState(prevState => ({
                editedQuantities: {
                    ...prevState.editedQuantities,
                    [partId]: ''
                }
            }));
            return;
        }
        
        const newQuantity = parseNumberInputValue(quantity);
        const part = editingOrder.purchasedParts.find(p => p.id === partId);
        if (!part) return;

        const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
        const vendorId = part.vendorId || (() => {
            const vendor = this.state.vendors.find(v => v.name === part.companyName);
            return vendor ? vendor.id : null;
        })();
        const vendorPart = partExpanded?.part?.vendorParts?.find(vp => vp.vendorId === vendorId);

        let newUnitCost = null;
        let newTotalPrice = null;

        if (vendorPart && vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
            const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
            const tier = newQuantity < sortedTiers[0].quantity
                ? sortedTiers[0]
                : [...sortedTiers].reverse().find(t => newQuantity >= t.quantity) || sortedTiers[sortedTiers.length - 1];
            if (tier) {
                newUnitCost = Number(tier.price);
                newTotalPrice = newUnitCost * newQuantity;
            }
        } else if (vendorPart && vendorPart.costType === 1) {
            newUnitCost = Number(vendorPart.basePrice / vendorPart.quantity) || 0;
            newTotalPrice = newUnitCost * newQuantity;
        }

        if (newUnitCost == null || newTotalPrice == null) {
            const currentUnitCost = this.state.editedUnitCosts && this.state.editedUnitCosts.hasOwnProperty(partId)
                ? this.state.editedUnitCosts[partId]
                : this.getUnitCost(part);
            newUnitCost = currentUnitCost;
            newTotalPrice = newQuantity * currentUnitCost;
        }

        this.setState(prevState => ({
            editedQuantities: {
                ...prevState.editedQuantities,
                [partId]: newQuantity
            },
            editedUnitCosts: {
                ...prevState.editedUnitCosts,
                [partId]: parseFloat(newUnitCost.toFixed(3))
            },
            editedTotalPrices: {
                ...prevState.editedTotalPrices,
                [partId]: parseFloat(newTotalPrice.toFixed(3))
            }
        }));
    };

    handleLineItemUnitCostChange = (partId, unitCost) => {
        // Allow empty string for free editing
        if (unitCost === '') {
            this.setState(prevState => ({
                editedUnitCosts: {
                    ...prevState.editedUnitCosts,
                    [partId]: ''
                }
            }));
            return;
        }
        
        const quantity = this.state.editedQuantities[partId] || 
                        editingOrder.purchasedParts.find(p => p.id === partId)?.quantity || 1;
        const totalPrice = (parseFloat(unitCost) || 0) * quantity;
        
        this.setState(prevState => ({
            editedUnitCosts: {
                ...prevState.editedUnitCosts,
                [partId]: parseFloat(unitCost) || 0
            },
            editedTotalPrices: {
                ...prevState.editedTotalPrices,
                [partId]: parseFloat(totalPrice.toFixed(3))
            }
        }));
    };

    handleLineItemTotalPriceChange = (partId, totalPrice) => {
        // Allow empty string for free editing
        if (totalPrice === '') {
            this.setState(prevState => ({
                editedTotalPrices: {
                    ...prevState.editedTotalPrices,
                    [partId]: ''
                }
            }));
            return;
        }
        
        const quantity = this.state.editedQuantities[partId] || 
                        editingOrder.purchasedParts.find(p => p.id === partId)?.quantity || 1;
        const unitCost = quantity > 0 ? (parseFloat(totalPrice) || 0) / quantity : 0;
        
        this.setState(prevState => ({
            editedTotalPrices: {
                ...prevState.editedTotalPrices,
                [partId]: parseFloat(totalPrice) || 0
            },
            editedUnitCosts: {
                ...prevState.editedUnitCosts,
                [partId]: parseFloat(unitCost.toFixed(3))
            }
        }));
    };

    handleLineItemVendorChange = (partId, newVendor) => {
        this.setState(prevState => ({
            editedCompanyNames: {
                ...prevState.editedCompanyNames,
                [partId]: newVendor ? newVendor.name : ''
            }
        }));
    };

    getUnitCost = (part) => {
        const partId = part.id;
        if (this.state.editedUnitCosts && this.state.editedUnitCosts.hasOwnProperty(partId)) {
            return Number(this.state.editedUnitCosts[partId]);
        }
        // Calculate unit cost from total price and quantity
        const quantity = part.quantity || 1;
        const unitCost = quantity > 0 ? (part.pricePaid || 0) / quantity : 0;
        return Number(unitCost);
    };

    getUnitCostDisplay = (part) => {
        return this.getUnitCost(part).toFixed(3);
    };

    getTotalPrice = (part) => {
        const partId = part.id;
        if (this.state.editedTotalPrices && this.state.editedTotalPrices.hasOwnProperty(partId)) {
            return Number(this.state.editedTotalPrices[partId]);
        }
        // Use the stored total price
        const totalPrice = part.pricePaid || 0;
        return Number(totalPrice);
    };

    getTotalPriceDisplay = (part) => {
        return this.getTotalPrice(part).toFixed(3);
    };

    handleDeleteLineItem = (partId) => {
        if (!window.confirm('Are you sure you want to remove this item from the purchase order?')) {
            return;
        }
        
        // Mark item for deletion instead of immediately deleting
        const deletedItems = [...(this.state.deletedLineItems || [])];
        if (!deletedItems.includes(partId)) {
            deletedItems.push(partId);
        }
        
        this.setState({ 
            deletedLineItems: deletedItems 
        });
    };

    handleNewLineItemChange = (index, field, value) => {
        const updatedPendingItems = [...this.state.pendingNewLineItems];
        const row = updatedPendingItems[index];
        
        if (field === 'quantity') {
            // Allow empty string for free editing
            if (value === '') {
                updatedPendingItems[index] = {
                    ...row,
                    partQuantity: '',
                    quantity: ''
                };
                this.setState({ pendingNewLineItems: updatedPendingItems });
                return;
            }
            
            const newQuantity = Math.max(0, parseNumberInputValue(value));
            
            // Update quantity
            updatedPendingItems[index] = {
                ...row,
                partQuantity: newQuantity,
                quantity: newQuantity
            };

            // Recalculate price based on new quantity if using tiered pricing
            if (row.vendorParts && row.vendorId) {
                const vendorPart = row.vendorParts.find(vp => vp.vendorId === row.vendorId);
                if (vendorPart && vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) { // Tiered pricing
                    // Sort tiers by quantity in ascending order to find the lowest applicable tier
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    // If quantity is below first threshold, use lowest tier
                    // Otherwise find the highest applicable tier
                    const tier = newQuantity < sortedTiers[0].quantity 
                        ? sortedTiers[0] 
                        : [...sortedTiers].reverse().find(t => newQuantity >= t.quantity) || sortedTiers[sortedTiers.length - 1];
                    
                                         if (tier) {
                         const unitPrice = Number(tier.price);
                         updatedPendingItems[index] = {
                             ...updatedPendingItems[index],
                             price: unitPrice.toFixed(3),
                             pricePaid: unitPrice.toFixed(3),
                             total: unitPrice * newQuantity
                         };
                    } else {
                        // If no tier found, just update the total with current price
                        updatedPendingItems[index] = {
                            ...updatedPendingItems[index],
                            total: row.price * newQuantity
                        };
                    }
                } else {
                    // For fixed pricing or no tiers, just update the total
                    updatedPendingItems[index] = {
                        ...updatedPendingItems[index],
                        total: row.price * newQuantity
                    };
                }
            } else {
                // If no vendor parts, just update the total
                updatedPendingItems[index] = {
                    ...updatedPendingItems[index],
                    total: row.price * newQuantity
                };
            }
        } else if (field === 'pricePaid') {
            // Allow empty string for free editing
            if (value === '') {
                updatedPendingItems[index] = {
                    ...row,
                    [field]: '',
                    price: '',
                    total: 0,
                    isOverridden: true
                };
                this.setState({ pendingNewLineItems: updatedPendingItems });
                return;
            }
            
            const newPrice = parseNumberInputValue(value);
            updatedPendingItems[index] = {
                ...row,
                [field]: newPrice,
                price: newPrice,
                total: newPrice * (row.partQuantity || row.quantity || 1),
                isOverridden: true
            };
        } else {
            updatedPendingItems[index] = {
                ...row,
                [field]: value
            };
        }
        
        this.setState({ pendingNewLineItems: updatedPendingItems });
    };

    handleAddNewLineItem = () => {
        const newPendingItems = [...this.state.pendingNewLineItems, { 
            id: uuid(),
            familyId: null,
            familyQuantity: 1,
            isFamilyRow: true,
            description: '',
            vendor: '',
            price: 0,
            total: 0,
            tempId: Date.now()
        }];
        this.setState({ 
            pendingNewLineItems: newPendingItems,
            addingNewLineItem: true 
        });
    };

    handleFamilyChange = (event, newValue, rowIndex) => {
        // This matches the exact logic from Purchases.js
        let purchasingPart = null;
        let purchasingFamily = null;
        if (newValue) {
            if (newValue.isFamily) {
                purchasingFamily = this.state.partFamilies.find(x => x.id === newValue.value);
            } else {
                purchasingPart = this.state.parts.find(x => x.part.id === newValue.value);
            }
            if (!purchasingPart && !purchasingFamily) return;
        }

        const pendingNewLineItems = [...this.state.pendingNewLineItems];
        const changingPart = pendingNewLineItems[rowIndex];

        if (purchasingPart) {
            changingPart.partId = !newValue ? null : newValue.value;
            changingPart.selectedOption = newValue;
            let vendors = [];
            purchasingPart.part.vendorParts.forEach((vendorPart) => {
                const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                if (vendor && vendors.indexOf(vendor) < 0) vendors.push(vendor);
            });
            changingPart.vendors = vendors;
            changingPart.vendorsList = vendors.map((vendor) => vendor.name);
            changingPart.isFamilyRow = false;
            changingPart.description = purchasingPart.part.description;
            changingPart.vendorParts = purchasingPart.part.vendorParts;
            
            // Use the part's selected vendor (like Purchases.js)
            const vendorPart = (this.state.purchaseOrderVendorId
                ? purchasingPart.part.vendorParts.find(x => x.vendorId === this.state.purchaseOrderVendorId)
                : null)
                || purchasingPart.part.vendorParts.find(x => x.vendorId === purchasingPart.part.selectedVendorId)
                || purchasingPart.part.vendorParts[0];
            const vendorName = vendorPart ? vendors.find(x => x.id === vendorPart.vendorId)?.name : '';
            let initialPrice = 0;
            let initialQuantity = 1;

            if (vendorPart && vendorPart.costType === 0 && vendorPart.tieredCostFormulas && vendorPart.tieredCostFormulas.length > 0) {
                // Find the selected tiered cost formula
                const selectedTier = vendorPart.tieredCostFormulas.find(t => t.isSelected);
                if (selectedTier) {
                    initialPrice = Number(selectedTier.price).toFixed(2);
                    initialQuantity = selectedTier.quantity;
                } else {
                    // Fallback to first tier if none selected
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    initialPrice = Number(sortedTiers[0].price).toFixed(2);
                    initialQuantity = sortedTiers[0].quantity;
                }
            } else if (vendorPart) {
                initialPrice = Number((vendorPart.basePrice / vendorPart.quantity)).toFixed(3) || 0;
                initialQuantity = vendorPart.quantity || initialQuantity;
            }

            changingPart.vendorId = vendorPart?.vendorId;
            changingPart.vendorName = vendorName;
            changingPart.price = initialPrice;
            changingPart.pricePaid = initialPrice;
            changingPart.partQuantity = initialQuantity;
            changingPart.quantity = initialQuantity;
            changingPart.total = initialPrice * initialQuantity;
            
            this.setState({pendingNewLineItems});
        } else if (purchasingFamily) {
            // Update the family row (keep it visible)
            changingPart.familyId = purchasingFamily.id;
            changingPart.familyQuantity = 1;
            changingPart.description = purchasingFamily.description;
            changingPart.isFamilyRow = true;
            changingPart.selectedOption = {
                ...newValue,
                family: purchasingFamily
            };
            
            // Remove any existing parts from this family, but keep the family row
            const filteredLogParts = pendingNewLineItems.filter(row => row.familyId !== purchasingFamily.id || row.isFamilyRow);
            
            // Find the index of the family row
            const familyRowIndex = filteredLogParts.findIndex(row => row.id === changingPart.id);
            
            // Get all parts in this family and their vendors
            const familyParts = this.state.parts.filter(part =>
                part.part.partToFamilies &&
                part.part.partToFamilies.some(ptf => ptf.familyId === purchasingFamily.id && !ptf.isImpliedPart)
            );
            
            // Add each part from the family as a separate line item AFTER the family row
            familyParts.forEach((part, index) => {
                const partToFamily = part.part.partToFamilies.find(ptf => ptf.familyId === purchasingFamily.id);
                const defaultQuantity = partToFamily ? partToFamily.purchaseQuantity : 1;
                
                let vendors = [];
                part.part.vendorParts.forEach(vendorPart => {
                    const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                    if (vendor && !vendors.some(v => v.id === vendor.id)) {
                        vendors.push(vendor);
                    }
                });
                
                const newLineItem = {
                    id: uuid(),
                    familyId: purchasingFamily.id,
                    familyQuantity: 1,
                    partId: part.part.id,
                    partQuantity: defaultQuantity,
                    quantity: defaultQuantity,
                    description: part.part.description,
                    vendor: vendors.map(vendor => vendor.name),
                    vendorId: null,
                    vendorName: '',
                    price: 0,
                    pricePaid: 0,
                    total: 0,
                    isFamilyRow: false,
                    isOverridden: false,
                    isFamilyPart: true,
                    vendorParts: part.part.vendorParts,
                    selectedOption: {
                        title: `${part.part.partId} rev ${part.part.revision}`,
                        value: part.part.id,
                        part: part.part
                    }
                };
                
                // Use the part's selected vendor (like Purchases.js)
                const vendorPart = (this.state.purchaseOrderVendorId
                    ? part.part.vendorParts.find(x => x.vendorId === this.state.purchaseOrderVendorId)
                    : null)
                    || part.part.vendorParts.find(x => x.vendorId === part.part.selectedVendorId)
                    || part.part.vendorParts[0];
                const vendorName = vendorPart ? vendors.find(x => x.id === vendorPart.vendorId)?.name : '';
                let initialPrice = 0;
                const purchaseQuantity = parseInt(partToFamily?.purchaseQuantity) || 1;
                const partQuantity = purchaseQuantity; // Initial family quantity is 1

                if (vendorPart && vendorPart.costType === 0 && vendorPart.tieredCostFormulas && vendorPart.tieredCostFormulas.length > 0) {
                    // Find the appropriate tier for the partQuantity
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    // Find the highest tier less than or equal to the quantity
                    const tier = [...sortedTiers].reverse().find(t => partQuantity >= t.quantity) || sortedTiers[0];
                    initialPrice = Number(tier.price).toFixed(2);
                } else if (vendorPart) {
                    initialPrice = Number((vendorPart.basePrice / vendorPart.quantity)).toFixed(3) || 0;
                }

                newLineItem.vendorId = vendorPart?.vendorId;
                newLineItem.vendorName = vendorName;
                newLineItem.price = initialPrice;
                newLineItem.pricePaid = initialPrice;
                newLineItem.partQuantity = defaultQuantity;
                newLineItem.quantity = defaultQuantity;
                newLineItem.total = initialPrice * defaultQuantity;
                newLineItem.isOverridden = false;
                
                // Insert parts AFTER the family row
                filteredLogParts.splice(familyRowIndex + 1 + index, 0, newLineItem);
            });
            
            this.setState({pendingNewLineItems: filteredLogParts});
        }
    };

    handleFamilyQuantityChange = (event, rowIndex) => {
        const newQuantity = event.target.value;
        const familyId = this.state.pendingNewLineItems[rowIndex].familyId;

        const updatedLogParts = this.state.pendingNewLineItems.map((row, index) => {
            if (index === rowIndex) {
                return { ...row, familyQuantity: newQuantity };
            }
            if (row.familyId === familyId && !row.isFamilyRow) {
                const part = this.state.parts.find(x => x.part.id === row.partId);
                if (!part) return row;
                
                // Get the PartToFamily relationship for this part and family
                const partFamilyRelation = part.part.partToFamilies.find(ptf => ptf.familyId === familyId);
                const purchaseQuantity = parseInt(partFamilyRelation?.purchaseQuantity) || 1;
                const partQuantity = purchaseQuantity * parseNumberInputValue(newQuantity);
                
                // Find the vendor part to check for tiered pricing
                const vendorPart = row.vendorParts?.find(vp => vp.vendorId === row.vendorId);
                let newPrice = Number(row.price).toFixed(3);
                
                if (vendorPart && vendorPart.costType === 0 && !row.isOverridden) { // Tiered pricing
                    // Sort tiers by quantity in ascending order to find the correct threshold
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    // Find the highest tier less than or equal to the quantity
                    const tier = [...sortedTiers].reverse().find(t => partQuantity >= t.quantity) || sortedTiers[0];
                    newPrice = Number(tier.price).toFixed(2);
                }
                
                return {
                    ...row,
                    partQuantity: partQuantity,
                    quantity: partQuantity,
                    price: parseFloat(newPrice),
                    pricePaid: parseFloat(newPrice),
                    total: newPrice * partQuantity,
                    isOverridden: false // Reset override flag when family quantity changes
                };
            }
            return row;
        });

        this.setState({ pendingNewLineItems: updatedLogParts });
    };

    handleNewLineItemVendorChange = (rowIndex, vendorId) => {
        const pendingNewLineItems = [...this.state.pendingNewLineItems];
        const changingRow = pendingNewLineItems[rowIndex];
        const vendor = this.state.vendors.find(x => x.id === vendorId);
        const vendorPart = changingRow.vendorParts?.find(vp => vp.vendorId === vendorId);

        if (vendorPart && vendor) {
            let newPrice = 0;
            let newQuantity = changingRow.partQuantity || changingRow.quantity || 1;

            if (vendorPart.costType === 0) { // Tiered pricing
                // Find the selected tiered cost formula
                const selectedTier = vendorPart.tieredCostFormulas.find(t => t.isSelected);
                if (selectedTier) {
                    newPrice = Number(selectedTier.price).toFixed(2);
                    // Only update quantity for individual parts, not family parts
                    if (!changingRow.isFamilyPart) {
                        newQuantity = selectedTier.quantity;
                    }
                } else {
                    // Fallback to first tier if none selected
                    const sortedTiers = [...vendorPart.tieredCostFormulas].sort((a, b) => a.quantity - b.quantity);
                    newPrice = Number(sortedTiers[0].price).toFixed(2);
                    // Only update quantity for individual parts, not family parts
                    if (!changingRow.isFamilyPart) {
                        newQuantity = sortedTiers[0].quantity;
                    }
                }
            } else { // Fixed pricing
                newPrice = Number(vendorPart.basePrice / vendorPart.quantity).toFixed(3) || 0;
                // Only update quantity for individual parts, not family parts
                if (!changingRow.isFamilyPart) {
                    newQuantity = vendorPart.quantity || newQuantity;
                }
            }

            changingRow.vendor = vendor.name;
            changingRow.vendorId = vendorId;
            changingRow.vendorName = vendor.name;
            changingRow.price = newPrice;
            changingRow.pricePaid = newPrice;
            changingRow.partQuantity = newQuantity;
            changingRow.quantity = newQuantity;
            changingRow.total = newPrice * newQuantity;
            changingRow.isOverridden = true;
        }
        
        this.setState({pendingNewLineItems});
    };

    getTieredPricingTooltip = (vendorPart) => {
        if (!vendorPart) return '';
        
        if (vendorPart.costType === 0 && vendorPart.tieredCostFormulas?.length > 0) {
            return vendorPart.tieredCostFormulas
                .sort((a, b) => a.quantity - b.quantity)
                .map(tier => `${tier.quantity}: $${tier.price.toFixed(3)} ea`)
                .join('\n');
        } else {
            return `${vendorPart.quantity}: $${vendorPart.basePrice?.toFixed(3) || '0.000'} ea`;
        }
    };

    showPurchaseOrderCopyTable = (purchaseOrder) => {
        const copyTableData = purchaseOrder.purchasedParts.map(part => {
            const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
            return {
                partNumber: partExpanded?.part.partId || 'Unknown',
                revision: partExpanded?.part.revision || '',
                quantity: part.quantity
            };
        }).sort((a, b) => {
            // Sort by partNumber first, then by revision
            const partNumberCompare = (a.partNumber || '').localeCompare(b.partNumber || '');
            if (partNumberCompare !== 0) {
                return partNumberCompare;
            }
            return (a.revision || '').localeCompare(b.revision || '');
        });
        
        this.setState({
            showCopyTable: true,
            copyTableData
        });
    };

    copyTableToClipboard = async () => {
        // Create HTML table string
        const tableHtml = `
            <table style="border-collapse: collapse; border: 1px solid black; font-family: Arial, sans-serif; font-size: 12px;">
                <thead>
                    <tr>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">Quantity</th>
                        <th style="border: 1px solid black; padding: 8px; background-color: #f0f0f0; text-align: center;">Item</th>
                    </tr>
                </thead>
                <tbody>
                    ${this.state.copyTableData.map(item => 
                        `<tr>
                            <td style="border: 1px solid black; padding: 8px; text-align: center;">${item.quantity}</td>
                            <td style="border: 1px solid black; padding: 8px; text-align: left;">${item.partNumber} rev ${item.revision}</td>
                        </tr>`
                    ).join('')}
                </tbody>
            </table>
        `;

        // Create plain text version as fallback
        const plainText = `Quantity\tItem\n${this.state.copyTableData.map(item => 
            `${item.quantity}\t${item.partNumber} rev ${item.revision}`
        ).join('\n')}`;

        try {
            // Try to copy HTML to clipboard
            if (window.ClipboardItem) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([tableHtml], { type: 'text/html' }),
                        'text/plain': new Blob([plainText], { type: 'text/plain' })
                    })
                ]);
                window.$alertSuccess('Table copied to clipboard!');
            } else {
                // Fallback if ClipboardItem is not available
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess('Table copied to clipboard as text!');
            }
        } catch (err) {
            // Fallback to plain text if HTML clipboard fails
            try {
                await navigator.clipboard.writeText(plainText);
                window.$alertSuccess('Table copied to clipboard as text!');
            } catch (fallbackErr) {
                window.$alertError('Failed to copy to clipboard. Please copy manually.');
                console.error('Clipboard error:', fallbackErr);
            }
        }
    };

    getInvoicesForLineItem = (purchasedPartId) => {
        return this.state.purchasedPartInvoices[purchasedPartId] || [];
    };

    getLineItemPaidStatus = (purchasedPartId) => {
        const purchasedPart = editingOrder?.purchasedParts?.find(part => part.id === purchasedPartId);
        if (purchasedPart?.isPaid) {
            return true;
        }

        const attachedInvoices = this.getInvoicesForLineItem(purchasedPartId);
        return attachedInvoices.some(attachment => attachment.invoice?.isPaid);
    };

    handleSetPurchaseOrderPaidStatus = async (isPaid) => {
        if (!editingOrder?.purchasedParts?.length) {
            window.$alertError('No line items found on this purchase order');
            return;
        }

        const lineItems = editingOrder.purchasedParts.filter(part => !(this.state.deletedLineItems || []).includes(part.id));
        if (lineItems.length === 0) {
            window.$alertError('No line items available to update');
            return;
        }

        this.setState({ updatingPaidStatus: true, paidStatusAction: isPaid ? 'mark' : 'unmark' });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            const updatePromises = lineItems.map(part =>
                partService.updatePurchaseLog({
                    ...part,
                    isPaid
                })
            );
            await Promise.all(updatePromises);
            await this.loadData();
            window.$alertSuccess(`Purchase order marked as ${isPaid ? 'paid' : 'unpaid'}`);
        } catch (error) {
            console.error(`Error marking purchase order as ${isPaid ? 'paid' : 'unpaid'}:`, error);
            window.$alertError(`Failed to mark purchase order as ${isPaid ? 'paid' : 'unpaid'}`);
        } finally {
            this.setState({ updatingPaidStatus: false, paidStatusAction: null });
        }
    };

    handleSetLineItemPaidStatus = async (lineItem, isPaid) => {
        this.setState({ updatingPaidStatus: true });
        try {
            const partService = ServiceLocator.Instance.getPartService();
            await partService.updatePurchaseLog({
                ...lineItem,
                isPaid
            });
            await this.loadData();
            window.$alertSuccess(`Line item marked as ${isPaid ? 'paid' : 'unpaid'}`);
        } catch (error) {
            console.error(`Error marking line item as ${isPaid ? 'paid' : 'unpaid'}:`, error);
            window.$alertError(`Failed to mark line item as ${isPaid ? 'paid' : 'unpaid'}`);
        } finally {
            this.setState({ updatingPaidStatus: false });
        }
    };

    getVisibleLineItems = () => {
        return (editingOrder?.purchasedParts || []).filter(part => !(this.state.deletedLineItems || []).includes(part.id));
    };

    areAllVisibleLineItemsPaid = () => {
        const lineItems = this.getVisibleLineItems();
        if (lineItems.length === 0) return false;
        return lineItems.every(part => this.getLineItemPaidStatus(part.id));
    };

    isAnyVisibleLineItemPaid = () => {
        const lineItems = this.getVisibleLineItems();
        return lineItems.some(part => this.getLineItemPaidStatus(part.id));
    };

    download = (url, filename) => {
        if (!url) return;
        
        // Use XMLHttpRequest to download the file properly with the correct filename
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        
        xhr.onload = function () {
            if (xhr.status === 200) {
                const blob = xhr.response;
                const hyperlinkElement = document.createElement("a");
                const objectUrl = URL.createObjectURL(blob);
                hyperlinkElement.href = objectUrl;
                // Ensure filename has proper extension - extract from URL if filename doesn't have one
                let finalFilename = filename || 'file';
                if (!finalFilename.includes('.')) {
                    // Try to get extension from URL
                    const urlMatch = url.match(/\.([a-zA-Z0-9]+)(\?|$)/);
                    if (urlMatch) {
                        finalFilename = `${finalFilename}.${urlMatch[1]}`;
                    }
                }
                hyperlinkElement.setAttribute("download", finalFilename);
                document.body.appendChild(hyperlinkElement);
                hyperlinkElement.click();
                document.body.removeChild(hyperlinkElement);
                // Clean up the object URL after a delay
                setTimeout(() => URL.revokeObjectURL(objectUrl), 100);
            } else {
                // Fallback: open in new tab if download fails
                window.open(url, '_blank');
            }
        };
        
        xhr.onerror = function () {
            // Fallback: open in new tab if request fails
            window.open(url, '_blank');
        };
        
        xhr.open('GET', url);
        // Set authorization header if needed (for authenticated requests)
        xhr.setRequestHeader('Accept', '*/*');
        xhr.send();
    };

    handleRemovePendingLineItem = (index) => {
        const updatedPendingItems = this.state.pendingNewLineItems.filter((_, i) => i !== index);
        this.setState({ 
            pendingNewLineItems: updatedPendingItems,
            addingNewLineItem: updatedPendingItems.length > 0
        });
    };

    editPart = (partExpanded, openInNewTab) => {
        if (!partExpanded?.part?.id) return;
        if (openInNewTab) window.open(`/admin/parts/${partExpanded.part.id}`);
        else globalProps.history.push(`/admin/parts/${partExpanded.part.id}`);
    };

    renderLinkedPartDisplay = (partExpanded) => {
        if (!partExpanded?.part) return 'Unknown';
        const canEdit = this.state.permissions?.includes('EditAssembliesAndParts');
        const inner = (
            <PartDisplay
                part={partExpanded.part}
                variant="table"
                classes={this.props.classes}
            />
        );
        if (!canEdit) return inner;
        return (
            <div
                style={{ cursor: 'pointer' }}
                onClick={(e) => this.editPart(partExpanded, e.ctrlKey || e.metaKey)}
                title="Click to open part (Ctrl+Click for new tab)"
            >
                {inner}
            </div>
        );
    };

    renderPartNumberTextLink = (partExpanded, displayText) => {
        if (!partExpanded?.part?.id || !this.state.permissions?.includes('EditAssembliesAndParts')) {
            return displayText;
        }
        return (
            <span
                role="link"
                tabIndex={0}
                style={{ cursor: 'pointer', fontWeight: 600 }}
                onClick={(e) => this.editPart(partExpanded, e.ctrlKey || e.metaKey)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        this.editPart(partExpanded, e.ctrlKey || e.metaKey);
                    }
                }}
                title="Click to open part (Ctrl+Click for new tab)"
            >
                {displayText}
            </span>
        );
    };

    renderReceiveDrawer = () => {
        if (!this.state.selectedPackingSlip) return null;

        return (
            <DrawerMenuRight
                open={this.state.receiveDrawerOpen}
                onClose={this.closeReceiveDrawer}
                title="Receive Packing Slip"
                subtitle={`${this.state.selectedPackingSlip.name || 'Unnamed'} - ${this.state.selectedPackingSlip.vendorName}`}
            >
                <div style={{padding: '20px'}}>
                    <div style={{marginBottom: '20px'}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.handleReceiveAll}
                            className="success-button-lg"
                            disabled={this.state.receivingPackingSlip}
                            style={{width: '100%', marginBottom: '12px'}}
                        >
                            Receive All Expected
                        </Button>
                    </div>
                    
                    <Typography variant="h6" style={{marginBottom: '16px'}}>
                        Or adjust quantities:
                    </Typography>
                    
                    {this.state.selectedPackingSlip.items?.sort((a, b) => {
                        const partA = this.state.parts.find(p => p.part.id === a.purchasedPart?.partId);
                        const partB = this.state.parts.find(p => p.part.id === b.purchasedPart?.partId);
                        const partIdA = partA ? partA.part.partId : 'Unknown Part';
                        const partIdB = partB ? partB.part.partId : 'Unknown Part';
                        return partIdA.localeCompare(partIdB);
                    }).map((item) => (
                        <div key={item.id} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 0',
                            borderBottom: '1px solid #eee'
                        }}>
                            <div style={{flex: 1, marginRight: '12px'}}>
                                <div style={{fontSize: '14px', fontWeight: 'bold'}}>
                                    {(() => {
                                        const part = this.state.parts.find(p => p.part.id === item.purchasedPart?.partId);
                                        const label = part ? part.part.partId : 'Unknown Part';
                                        return part ? this.renderPartNumberTextLink(part, label) : label;
                                    })()}
                                </div>
                                <div style={{fontSize: '12px', color: '#666'}}>
                                    Expected: {item.quantityExpected}
                                </div>
                            </div>
                            <TextField
                                type="number"
                                value={getNumberInputDisplayValue(this.state.receivedQuantities[item.id] || 0)}
                                onChange={(e) => this.handleQuantityChange(item.id, e.target.value)}
                                inputProps={{min: 0}}
                                variant="outlined"
                                size="small"
                                style={{width: '80px'}}
                            />
                        </div>
                    ))}
                    
                    <div style={{marginTop: '20px', display: 'flex', gap: '12px'}}>
                        <Button
                            onClick={this.closeReceiveDrawer}
                            variant="outlined"
                            style={{flex: 1}}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={this.handleReceiveCustom}
                            variant="contained"
                            color="primary"
                            disabled={this.state.receivingPackingSlip}
                            style={{flex: 1}}
                        >
                            {this.state.receivingPackingSlip ? <CircularProgress size={20} /> : 'Receive'}
                        </Button>
                    </div>
                </div>
            </DrawerMenuRight>
        );
    };

    render() {
        const {classes} = this.props;

        if (this.state.loading || !editingOrder) {
            return (
                <DashboardTemplate title="Edit Purchase Order" onSyncDatabase={this.loadData}>
                    <div className="main-dashboard-root">
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="Edit Purchase Order" onSyncDatabase={this.loadData}>
                <div className="main-dashboard-root">
                    <div className={classes.wrapper}>
                        {/* Header with Back/Save buttons and centered PO number */}
                        <div style={{marginBottom: 0}}>
                            <div style={{position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 0}}>
                                {/* Left: Back and Save buttons */}
                                <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-start'}}>
                                    <Button 
                                        variant="outlined"
                                        color="primary"
                                        onClick={this.handleBack}
                                        className="success-button-lg"
                                    >
                                        Back to Purchases
                                    </Button>
                                    {this.state.savingPurchaseOrder === true ?
                                        <CircularProgress/>
                                        :
                                        <>
                                            <Button
                                                onClick={this.handleSave}
                                                className="save-button-lg"
                                                style={{
                                                    backgroundColor: '#7fff84',
                                                    color: 'black'
                                                }}
                                            >
                                                Save Changes
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={this.deletePurchaseOrder}
                                                className="delete-button-lg"
                                                style={{backgroundColor: '#d32f2f', color: 'white'}}
                                            >
                                                Delete Purchase Order
                                            </Button>
                                        </>
                                    }
                                </div>
                                
                                {/* Center: PO number - Absolutely centered */}
                                <div style={{position: 'absolute', left: '50%', transform: 'translateX(-50%)'}}>
                                    <h1 style={{margin: 0, textAlign: 'center', color: 'white'}}>PO #{editingOrder.purchaseOrderNumber}</h1>
                                </div>
                                
                                {/* Right: Copy table button */}
                                <div style={{flex: '1', display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'flex-end'}}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => this.showPurchaseOrderCopyTable(editingOrder)}
                                        className="success-button-lg"
                                    >
                                        Show Copy Table for Email
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '20px', marginBottom: 20, alignItems: 'flex-start'}}>
                            {/* Column 1: Order Details & Line Items */}
                            <div style={{flex: '1', minWidth: '300px', maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0}}>
                                    <h2 style={{marginTop: 0}}>Order Details</h2>
                                    
                                    <Typography variant="body1" style={{marginBottom: '15px'}}>
                                        <strong>Vendor:</strong> {editingOrder.purchasedParts?.[0]?.companyName || 'Multiple/Unknown'}
                                    </Typography>
                                    
                                    <Typography variant="body1" style={{marginBottom: '15px'}}>
                                        <strong>Created by:</strong> {(() => {
                                            const user = editingOrder.userId ? this.state.adminUsers.find(u => u.id === editingOrder.userId) : null;
                                            return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
                                        })()}
                                    </Typography>
                                    
                                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                        <DatePicker
                                            label="Placed On"
                                            value={dayjs(editingOrder.placedOn)}
                                            onChange={(newValue) => {
                                                editingOrder.placedOn = newValue;
                                                this.forceUpdate();
                                            }}
                                            renderInput={(params) => <TextField {...params} variant="outlined" style={{width: '48%', marginBottom: 20}} />}
                                        />
                                    </MuiPickersUtilsProvider>
                                    <TextField
                                        label="Vendor Order Number"
                                        value={this.state.editingVendorOrderNumber}
                                        onChange={(event) => this.setState({editingVendorOrderNumber: event.target.value})}
                                        variant="outlined"
                                        style={{width: '48%', marginBottom: 20, marginLeft: 10}}
                                        inputProps={{maxLength: 200}}
                                    />

                                    {/* Line Items Section */}
                                    <div style={{borderTop: '1px solid #e0e0e0', paddingTop: '20px'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
                                            <h3 style={{margin: 0}}>Line Items ({editingOrder.purchasedParts.filter(part => !(this.state.deletedLineItems || []).includes(part.id)).length})</h3>
                                            <Button
                                                color="primary"
                                                onClick={this.handleEditLineItems}
                                                className="success-button-lg"
                                            >
                                                Edit Line Items
                                            </Button>
                                        </div>

                                        <div className="parts-table-container" style={{maxHeight: '650px', overflowY: 'auto'}}>
                                            <table className="parts-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{minWidth: 200}}>Part</th>
                                                        <th style={{minWidth: 30}}>Qty</th>
                                                        <th>Price</th>
                                                        <th>Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {editingOrder.purchasedParts
                                                        .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                        .sort((a, b) => {
                                                            const partA = this.state.parts.find(x => x.part.id === a.partId);
                                                            const partB = this.state.parts.find(x => x.part.id === b.partId);
                                                            const partNumberA = partA?.part.partId || '';
                                                            const partNumberB = partB?.part.partId || '';
                                                            return partNumberA.localeCompare(partNumberB);
                                                        })
                                                        .map((part) => {
                                                        const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                        return (
                                                            <tr key={part.id}>
                                                                <td className="description-ellipsis text-left">
                                                                    {partExpanded?.part
                                                                        ? this.renderLinkedPartDisplay(partExpanded)
                                                                        : 'Unknown'}
                                                                </td>
                                                                <td>{part.quantity}</td>
                                                                <td style={{minWidth: 65}}>${((part.pricePaid || 0) / part.quantity).toFixed(2)}</td>
                                                                <td style={{minWidth: 65}}>${(part.pricePaid || 0).toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr style={{borderTop: '2px solid #e0e0e0', fontWeight: 'bold'}}>
                                                        <td colSpan="3" style={{textAlign: 'right', padding: '10px'}}>Total:</td>
                                                        <td style={{padding: '10px'}}>${editingOrder.purchasedParts
                                                            .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                            .reduce((sum, part) => sum + (part.pricePaid || 0), 0)
                                                            .toFixed(2)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Column 2: Invoices */}
                            <div style={{flex: '1', minWidth: '300px', maxWidth: '50%', display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0}}>
                                    <h2 style={{marginTop: 0}}>Invoices</h2>
                                    
                                    <div style={{marginBottom: 20, display: 'flex', gap: '10px'}}>
                                        {!this.areAllVisibleLineItemsPaid() && (
                                            <Button
                                                onClick={() => this.handleSetPurchaseOrderPaidStatus(true)}
                                                className="success-button-lg"
                                                disabled={this.state.updatingPaidStatus}
                                            >
                                                {this.state.updatingPaidStatus && this.state.paidStatusAction === 'mark' ? (
                                                    <>
                                                        <CircularProgress size={16} style={{ marginRight: 8, marginLeft: 8, color: 'inherit' }} />
                                                    </>
                                                ) : (
                                                    'Mark PO Paid'
                                                )}
                                            </Button>
                                        )}
                                        {this.isAnyVisibleLineItemPaid() && (
                                            <Button
                                                onClick={() => this.handleSetPurchaseOrderPaidStatus(false)}
                                                className="cancel-button-lg"
                                                disabled={this.state.updatingPaidStatus}
                                            >
                                                {this.state.updatingPaidStatus && this.state.paidStatusAction === 'unmark' ? (
                                                    <>
                                                        <CircularProgress size={16} style={{ marginRight: 8, marginLeft: 8, color: 'inherit' }} />
                                                    </>
                                                ) : (
                                                    'Mark PO Unpaid'
                                                )}
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() => this.handleCreateLineItemInvoice(editingOrder.id)}
                                            className="success-button-lg"
                                        >Create Line Item Invoice</Button>
                                        <Button onClick={() => this.handleAttachExistingLineItemInvoice(editingOrder.id)} className="cancel-button-lg">Attach Existing Invoice</Button>
                                    </div>
                                    
                                    <div className="parts-table-container">
                                        <table className="parts-table">
                                            <thead>
                                                <tr>
                                                    <th>Part Number</th>
                                                    <th>Ordered</th>
                                                    <th>Received</th>
                                                    <th>Remaining</th>
                                                    <th>Paid Status</th>
                                                    <th>Invoice</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {editingOrder.purchasedParts
                                                    .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                    .sort((a, b) => {
                                                        const partA = this.state.parts.find(x => x.part.id === a.partId);
                                                        const partB = this.state.parts.find(x => x.part.id === b.partId);
                                                        const partNumberA = partA?.part.partId || '';
                                                        const partNumberB = partB?.part.partId || '';
                                                        return partNumberA.localeCompare(partNumberB);
                                                    })
                                                    .map((part) => {
                                                    const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                    const isPaid = this.getLineItemPaidStatus(part.id);
                                                    const attachedInvoices = this.getInvoicesForLineItem(part.id);
                                                    const received = part.quantityReceived || 0;
                                                    const remaining = Math.max(0, part.quantity - received);
                                                    
                                                    return (
                                                        <tr key={part.id}>
                                                            <td>
                                                                {this.renderPartNumberTextLink(
                                                                    partExpanded,
                                                                    partExpanded?.part
                                                                        ? `${partExpanded.part.partId} rev ${partExpanded.part.revision}`
                                                                        : 'Unknown'
                                                                )}
                                                            </td>
                                                            <td>{part.quantity}</td>
                                                            <td>{received}</td>
                                                            <td>{remaining}</td>
                                                            <td>
                                                                <span style={{
                                                                    color: isPaid ? '#4caf50' : '#ff9800', 
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {isPaid ? 'PAID' : 'UNPAID'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {attachedInvoices.length > 0 ? (
                                                                    attachedInvoices.map(inv => (
                                                                        <div key={inv.invoice.id} style={{marginBottom: 2}}>
                                                                            <span 
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    color: inv.invoice.isPaid ? '#4caf50' : '#ff9800',
                                                                                    textDecoration: 'underline'
                                                                                }}
                                                                                onClick={(event) => this.handleViewInvoice(inv.invoice, event)}
                                                                            >
                                                                                {inv.invoice.invoiceNumber}
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <span style={{color: '#999', fontStyle: 'italic'}}>No invoice</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {isPaid ? (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        className="cancel-button"
                                                                        disabled={this.state.updatingPaidStatus}
                                                                        onClick={() => this.handleSetLineItemPaidStatus(part, false)}
                                                                    >
                                                                        Unmark Paid
                                                                    </Button>
                                                                ) : (
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        className="success-button"
                                                                        disabled={this.state.updatingPaidStatus}
                                                                        onClick={() => this.handleSetLineItemPaidStatus(part, true)}
                                                                    >
                                                                        Mark Paid
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 10}}>
                                    <h2 style={{marginTop: 0}}>Receiving Actions</h2>
                                    {/* Receiving Actions Section */}
                                    <div style={{ marginTop: '0px' }}>
                                        {this.state.loadingReceivingActions ? (
                                            <div style={{textAlign: 'center', padding: '20px'}}>
                                                <CircularProgress size={24} />
                                                <Typography variant="body2" style={{marginTop: 10, color: '#666'}}>
                                                    Loading receiving actions...
                                                </Typography>
                                            </div>
                                        ) : this.state.receivingActions && this.state.receivingActions.length > 0 ? (
                                            <div className="parts-table-container">
                                                <table className="parts-table">
                                                    <thead>
                                                        <tr>
                                                            <SortableTableHeader field="receivedDate" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 80}}>Date</SortableTableHeader>
                                                            <SortableTableHeader field="partNumber" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 150}}>Part Number</SortableTableHeader>
                                                            <SortableTableHeader field="quantityReceived" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 55}}>Quantity</SortableTableHeader>
                                                            <SortableTableHeader field="packingSlip" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 200}}>Packing Slip</SortableTableHeader>
                                                            <SortableTableHeader field="userName" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 120}}>Received By</SortableTableHeader>
                                                            <SortableTableHeader field="notes" sortField={this.state.receivingActionsSortField} sortDirection={this.state.receivingActionsSortDirection} onSort={this.handleReceivingActionsSort} style={{minWidth: 200}}>Notes</SortableTableHeader>
                                                            <th style={{minWidth: 80}}>File</th>
                                                            <th style={{minWidth: 60}}>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {this.getSortedReceivingActions().map((action) => {
                                                            // Receiving actions have partNumber, partRevision, and partDescription directly
                                                            // Fallback to lookup if those aren't available
                                                            let partNumber = 'Unknown';
                                                            let partExpandedForLink = null;
                                                            
                                                            // Debug logging
                                                            if (!action.partNumber) {
                                                                console.log('Receiving action missing partNumber:', {
                                                                    actionId: action.id,
                                                                    purchasedPartId: action.purchasedPartId,
                                                                    purchasedPart: action.purchasedPart,
                                                                    packingSlipItem: action.packingSlipItem,
                                                                    action: action
                                                                });
                                                            }
                                                            
                                                            if (action.partNumber) {
                                                                partNumber = action.partRevision ? 
                                                                    `${action.partNumber} rev ${action.partRevision}` : 
                                                                    action.partNumber;
                                                                partExpandedForLink = this.state.parts.find((p) =>
                                                                    p.part.partId === action.partNumber &&
                                                                    (action.partRevision == null || action.partRevision === '' ||
                                                                        String(p.part.revision) === String(action.partRevision))
                                                                ) || this.state.parts.find((p) => p.part.partId === action.partNumber);
                                                            } else {
                                                                // Fallback: try to find via purchased part lookup
                                                                const actionPurchasedPartId = action.purchasedPartId || 
                                                                                             action.purchasedPart?.id || 
                                                                                             action.PurchasedPartId ||
                                                                                             action.packingSlipItem?.purchasedPartId ||
                                                                                             action.packingSlipItem?.purchasedPart?.id;
                                                                
                                                                const purchasedPart = editingOrder.purchasedParts.find(pp => pp.id === actionPurchasedPartId);
                                                                
                                                                if (purchasedPart) {
                                                                    const partExpanded = this.state.parts.find(p => p.part.id === purchasedPart.partId);
                                                                    if (partExpanded) {
                                                                        partExpandedForLink = partExpanded;
                                                                        partNumber = partExpanded.part.revision ? 
                                                                            `${partExpanded.part.partId} rev ${partExpanded.part.revision}` : 
                                                                            partExpanded.part.partId;
                                                                    }
                                                                } else if (action.packingSlipItem) {
                                                                    // Try to get part info from packing slip item
                                                                    const itemPurchasedPartId = action.packingSlipItem.purchasedPartId || 
                                                                                                action.packingSlipItem.purchasedPart?.id;
                                                                    const itemPurchasedPart = editingOrder.purchasedParts.find(pp => pp.id === itemPurchasedPartId);
                                                                    if (itemPurchasedPart) {
                                                                        const partExpanded = this.state.parts.find(p => p.part.id === itemPurchasedPart.partId);
                                                                        if (partExpanded) {
                                                                            partExpandedForLink = partExpanded;
                                                                            partNumber = partExpanded.part.revision ? 
                                                                                `${partExpanded.part.partId} rev ${partExpanded.part.revision}` : 
                                                                                partExpanded.part.partId;
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                            
                                                            return (
                                                                <tr key={action.id}>
                                                                    <td>
                                                                        {action.receivedDate ? 
                                                                            new Date(action.receivedDate).toISOString().split('T')[0] : 
                                                                            action.dateCreated ? 
                                                                                new Date(action.dateCreated).toISOString().split('T')[0] : 
                                                                                '-'
                                                                        }
                                                                    </td>
                                                                    <td className="text-left">
                                                                        {partExpandedForLink
                                                                            ? this.renderPartNumberTextLink(partExpandedForLink, partNumber)
                                                                            : partNumber}
                                                                    </td>
                                                                    <td>{action.quantityReceived || 0}</td>
                                                                    <td style={{ textAlign: action.packingSlip ? 'left' : 'center' }}>
                                                                        {action.packingSlip ? (
                                                                            <span style={{
                                                                                fontWeight: 'bold',
                                                                                color: 'black',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => this.handlePackingSlipSelect(action.packingSlip)}>
                                                                                {(() => {
                                                                                    const packingSlipName = action.packingSlip.name || `PS-${action.packingSlip.id?.substring(0, 8)}`;
                                                                                    // Remove box number from name for display
                                                                                    return packingSlipName;
                                                                                })()}
                                                                            </span>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td>{action.userName || '-'}</td>
                                                                    <td className="text-left">{action.notes || '-'}</td>
                                                                    <td>
                                                                        {action.packingSlip?.fileUrl ? (
                                                                            <Button 
                                                                                size="small" 
                                                                                onClick={() => {
                                                                                    const fileUrl = action.packingSlip.fileUrl;
                                                                                    // Try multiple possible locations for filename
                                                                                    const fileName = action.packingSlip.fileName || 
                                                                                                   action.packingSlip.name || 
                                                                                                   'packing-slip-file';
                                                                                    console.log('Downloading file:', { fileUrl, fileName, packingSlip: action.packingSlip });
                                                                                    this.download(fileUrl, fileName);
                                                                                }}
                                                                            >
                                                                                View
                                                                            </Button>
                                                                        ) : '-'}
                                                                    </td>
                                                                    <td>
                                                                        {this.isDirectReceiveAction(action) ? (
                                                                            <Button
                                                                                size="small"
                                                                                color="secondary"
                                                                                onClick={() => this.handleDeleteDirectReceiveAction(action)}
                                                                                title="Delete direct receive"
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                            </Button>
                                                                        ) : '-'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <Typography color="textSecondary">No receiving actions found for this purchase order</Typography>
                                        )}
                                    </div>
                                </div>

                                {/* Packing Slips Section */}
                                <div className="container" style={{padding: '20px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: 0, marginTop: 10}}>
                                    <h2 style={{marginTop: 0}}>Packing Slips</h2>
                                    <div style={{ marginTop: '0px' }}>
                                        {editingOrder.packingSlips && editingOrder.packingSlips.length > 0 ? (
                                            <div className="parts-table-container">
                                                <table className="parts-table">
                                                    <thead>
                                                        <tr>
                                                            <th style={{minWidth: 200}}>Name</th>
                                                            <th style={{minWidth: 130}}>Date Created</th>
                                                            <th style={{minWidth: 100}}>Tracking #</th>
                                                            <th style={{minWidth: 100}}>Status</th>
                                                            <th style={{minWidth: 100}}>File</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {editingOrder.packingSlips.map((packingSlip) => {
                                                            const packingSlipName = packingSlip.name || `PS-${packingSlip.id?.substring(0, 8)}`;
                                                            const packingSlipFullyReceived = isPackingSlipFullyReceived(packingSlip);
                                                            
                                                            return (
                                                                <tr key={packingSlip.id} className={`packing-slip-row ${packingSlipFullyReceived ? 'received' : packingSlip.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                                    <td className="text-left">
                                                                        <span className="packing-slip-name"
                                                                            onClick={() => this.handlePackingSlipSelect(packingSlip)}>
                                                                            {packingSlipName}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {packingSlip.dateCreated ? 
                                                                            new Date(packingSlip.dateCreated).toISOString().split('T')[0] : 
                                                                            '-'
                                                                        }
                                                                    </td>
                                                                    <td>{packingSlip.trackingNumber || '-'}</td>
                                                                    <td>
                                                                        <span className={`packing-slip-status ${packingSlipFullyReceived ? 'received' : packingSlip.hasAnyReceiving ? 'partially-received' : ''}`}>
                                                                            {packingSlipFullyReceived ? 'Received' : packingSlip.hasAnyReceiving ? 'Partially Received' : 'Pending'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        {packingSlip.fileUrl ? (
                                                                            <span className="packing-slip-file"
                                                                                onClick={() => {
                                                                                    const fileUrl = packingSlip.fileUrl;
                                                                                    const fileName = packingSlip.fileName || packingSlip.name || 'packing-slip-file';
                                                                                    this.download(fileUrl, fileName);
                                                                                }}>
                                                                                {packingSlip.fileName || 'Download'}
                                                                            </span>
                                                                        ) : '-'}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            <Typography color="textSecondary">No packing slips for this purchase order</Typography>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Invoice Creation Drawer */}
                        <DrawerMenuRight
                            open={this.state.createLineItemInvoiceOpen}
                            onClose={this.handleCloseCreateLineItemInvoice}
                            title="Create New Invoice"
                            customWidth={800}
                        >
                            <div style={{padding: '20px'}}>
                                <TextField
                                    label="Invoice Number"
                                    value={this.state.newInvoiceNumber}
                                    onChange={(e) => this.setState({newInvoiceNumber: e.target.value})}
                                    variant="outlined" fullWidth style={{marginBottom: 20}}
                                />
                                
                                <div style={{marginBottom: 20}}>
                                    <Typography variant="body1" style={{marginBottom: 10}}>Upload Invoice File:</Typography>
                                    <div
                                        style={{
                                            border: '2px dashed #ccc',
                                            borderRadius: '4px',
                                            padding: '0',
                                            textAlign: 'center',
                                            backgroundColor: '#fafafa',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            minHeight: '51px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative'
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.borderColor = '#1976d2';
                                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.borderColor = '#ccc';
                                            e.currentTarget.style.backgroundColor = '#fafafa';
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.currentTarget.style.borderColor = '#ccc';
                                            e.currentTarget.style.backgroundColor = '#fafafa';
                                            const files = e.dataTransfer.files;
                                            if (files.length > 0) {
                                                const file = files[0];
                                                if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
                                                    this.setState({newInvoiceFile: file});
                                                }
                                            }
                                        }}
                                        onClick={() => document.getElementById('file-input').click()}
                                    >
                                        {this.state.newInvoiceFile ? (
                                            <div>
                                                <div style={{fontSize: '14px', color: '#1976d2', fontWeight: 'bold'}}>
                                                    ✓ File Selected
                                                </div>
                                                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                                                    {this.state.newInvoiceFile.name}
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{fontSize: '14px', color: '#666'}}>
                                                    Drag & drop or click to upload
                                                </div>
                                            </div>
                                        )}
                                        <input
                                            id="file-input"
                                            type="file"
                                            onChange={(e) => this.setState({newInvoiceFile: e.target.files[0]})}
                                            style={{display: 'none'}}
                                        />
                                    </div>
                                </div>
                                
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                    <Typography variant="h6">Select Line Items:</Typography>
                                    <div style={{display: 'flex', gap: '10px'}}>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={this.handleSelectAllLineItems}
                                            style={{fontSize: '12px', padding: '4px 8px'}}
                                        >
                                            Select All
                                        </Button>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={this.handleSelectNoneLineItems}
                                            style={{fontSize: '12px', padding: '4px 8px'}}
                                        >
                                            Select None
                                        </Button>
                                    </div>
                                </div>
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th style={{width: '60px'}}>Select</th>
                                                <th>Part Number</th>
                                                <th>Description</th>
                                                <th>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editingOrder.purchasedParts
                                                .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                .map((part) => {
                                                const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                return (
                                                    <tr key={part.id}>
                                                        <td style={{textAlign: 'center'}}>
                                                            <Checkbox
                                                                checked={this.state.selectedLineItems[part.id] || false}
                                                                onChange={(e) => this.handleLineItemSelection(part.id, e.target.checked, e)}
                                                                color="primary" style={{padding: 0}}
                                                            />
                                                        </td>
                                                        <td>
                                                            {this.renderPartNumberTextLink(
                                                                partExpanded,
                                                                partExpanded?.part?.partId || 'Unknown'
                                                            )}
                                                        </td>
                                                        <td className="description-ellipsis text-left">{partExpanded?.part.description || 'Unknown'}</td>
                                                        <td>{part.quantity}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                
                                <div style={{marginTop: 30, display: 'flex', gap: '15px'}}>
                                    <Button onClick={this.handleCloseCreateLineItemInvoice} className="cancel-button-lg" style={{flex: 1}}>Cancel</Button>
                                    <Button onClick={this.handleSaveCreateLineItemInvoice} className="success-button-lg" style={{flex: 1}}>{this.state.savingInvoice ? "Creating..." : "Create Invoice"}</Button>
                                </div>
                            </div>
                        </DrawerMenuRight>

                        {/* Attach Existing Invoice Drawer */}
                        <DrawerMenuRight
                            open={this.state.attachExistingInvoiceToLineItemsOpen}
                            onClose={this.handleCloseAttachExistingInvoiceToLineItems}
                            title="Attach Existing Invoice"
                            customWidth={800}
                        >
                            <div style={{padding: '20px'}}>
                                <FormControl variant="outlined" fullWidth style={{marginBottom: 20}}>
                                    <InputLabel>Select Invoice</InputLabel>
                                    <Select
                                        value={this.state.selectedExistingInvoiceId}
                                        onChange={(e) => this.setState({selectedExistingInvoiceId: e.target.value})}
                                        label="Select Invoice"
                                    >
                                        {this.state.invoices.map(invoice => (
                                            <MenuItem key={invoice.id} value={invoice.id}>
                                                {invoice.invoiceNumber} - {invoice.isPaid ? 'PAID' : 'UNPAID'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                                    <Typography variant="h6">Select Line Items:</Typography>
                                    <div style={{display: 'flex', gap: '10px'}}>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={this.handleSelectAllLineItems}
                                            style={{fontSize: '12px', padding: '4px 8px'}}
                                        >
                                            Select All
                                        </Button>
                                        <Button 
                                            size="small" 
                                            variant="outlined" 
                                            onClick={this.handleSelectNoneLineItems}
                                            style={{fontSize: '12px', padding: '4px 8px'}}
                                        >
                                            Select None
                                        </Button>
                                    </div>
                                </div>
                                <div className="parts-table-container">
                                    <table className="parts-table">
                                        <thead>
                                            <tr>
                                                <th style={{width: '60px'}}>Select</th>
                                                <th>Part Number</th>
                                                <th>Description</th>
                                                <th>Quantity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editingOrder.purchasedParts
                                                .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                .map((part) => {
                                                const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                return (
                                                    <tr key={part.id}>
                                                        <td style={{textAlign: 'center'}}>
                                                            <Checkbox
                                                                checked={this.state.selectedLineItems[part.id] || false}
                                                                onChange={(e) => this.handleLineItemSelection(part.id, e.target.checked, e)}
                                                                color="primary" style={{padding: 0}}
                                                            />
                                                        </td>
                                                        <td>
                                                            {this.renderPartNumberTextLink(
                                                                partExpanded,
                                                                partExpanded?.part?.partId || 'Unknown'
                                                            )}
                                                        </td>
                                                        <td className="description-ellipsis text-left">{partExpanded?.part.description || 'Unknown'}</td>
                                                        <td>{part.quantity}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                
                                <div style={{marginTop: 30, display: 'flex', gap: '15px'}}>
                                    <Button onClick={this.handleCloseAttachExistingInvoiceToLineItems} className="cancel-button-lg" style={{flex: 1}}>Cancel</Button>
                                    <Button onClick={this.handleSaveAttachExistingInvoiceToLineItems} className="success-button-lg" style={{flex: 1}}>{this.state.savingInvoice ? "Attaching..." : "Attach Invoice"}</Button>
                                </div>
                            </div>
                        </DrawerMenuRight>

                        {/* Copy Table Dialog */}
                        <Dialog 
                            open={this.state.showCopyTable} 
                            onClose={() => this.setState({showCopyTable: false})}
                            maxWidth="sm"
                            fullWidth
                        >
                            <DialogTitle>
                                Copy Table for Email
                            </DialogTitle>
                            <DialogContent>
                                <table style={{
                                    margin: '0 auto',
                                    borderCollapse: 'collapse',
                                    border: '1px solid black',
                                    fontFamily: 'Arial, sans-serif',
                                    fontSize: '12px'
                                }}>
                                    <thead>
                                        <tr>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center'
                                            }}>
                                                Quantity
                                            </th>
                                            <th style={{
                                                border: '1px solid black',
                                                padding: '8px',
                                                backgroundColor: '#f0f0f0',
                                                textAlign: 'center'
                                            }}>
                                                Item
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...this.state.copyTableData].sort((a, b) => {
                                            // Sort by partNumber first, then by revision
                                            const partNumberCompare = (a.partNumber || '').localeCompare(b.partNumber || '');
                                            if (partNumberCompare !== 0) {
                                                return partNumberCompare;
                                            }
                                            return (a.revision || '').localeCompare(b.revision || '');
                                        }).map((item, index) => (
                                            <tr key={index}>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'center'
                                                }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{
                                                    border: '1px solid black',
                                                    padding: '8px',
                                                    textAlign: 'left'
                                                }}>
                                                    {item.partNumber} rev {item.revision}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.copyTableToClipboard} className="success-button-lg">Copy Table</Button>
                                <Button onClick={() => this.setState({showCopyTable: false})} className="cancel-button-lg">Close</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Receive Packing Slip Drawer */}
                        {this.renderReceiveDrawer()}

                        {/* Line Items Editing Drawer */}
                        <DrawerMenuRight
                            open={this.state.lineItemsDrawerOpen}
                            onClose={this.handleCloseLineItemsDrawer}
                            title={`Edit Line Items - PO #${editingOrder.purchaseOrderNumber}`}
                            customWidth={1300}
                            hasUnsavedChanges={Object.keys(this.state.editedQuantities).length > 0 || Object.keys(this.state.editedTotalPrices).length > 0 || Object.keys(this.state.editedCompanyNames).length > 0 || this.state.pendingNewLineItems.length > 0 || (this.state.deletedLineItems || []).length > 0}
                        >
                            <div className="parts-table-container">
                                <table className="parts-table">
                                    <thead>
                                        <tr>
                                            <th colSpan={2} style={{minWidth: 100}}>Family</th>
                                            <th colSpan={3} style={{minWidth: 500}}>Part</th>
                                            <th style={{minWidth: 50}}></th>
                                        </tr>
                                        <tr>
                                            <th style={{minWidth: 300}}>PN + Description</th>
                                            <th style={{minWidth: 80}}>Qty</th>
                                            <th style={{minWidth: 225}}>Vendor</th>
                                            <th style={{minWidth: 120}}>Price</th>
                                            <th style={{minWidth: 90}}>Total</th>
                                            <th style={{minWidth: 80}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Existing line items */}
                                        {editingOrder.purchasedParts
                                            .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                            .sort((a, b) => {
                                                const partA = this.state.parts.find(x => x.part.id === a.partId);
                                                const partB = this.state.parts.find(x => x.part.id === b.partId);
                                                const partNumberA = partA?.part.partId || '';
                                                const partNumberB = partB?.part.partId || '';
                                                return partNumberA.localeCompare(partNumberB);
                                            })
                                            .map((part, index) => {
                                            const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                            const editedQuantity = this.state.editedQuantities[part.id] || part.quantity;
                                            const unitCost = this.getUnitCost(part);
                                            const totalPrice = this.getTotalPrice(part);
                                            
                                            return (
                                                <tr key={part.id}>
                                                    <td>
                                                        {partExpanded?.part ? (
                                                            <PartDisplay
                                                                part={partExpanded.part}
                                                                variant="table"
                                                                classes={this.props.classes}
                                                            />
                                                        ) : (
                                                            'Unknown'
                                                        )}
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            type="number"
                                                            value={this.state.editedQuantities && this.state.editedQuantities.hasOwnProperty(part.id) 
                                                                ? this.state.editedQuantities[part.id] 
                                                                : part.quantity}
                                                            onChange={(e) => this.handleLineItemQuantityChange(part.id, e.target.value)}
                                                            variant="outlined"
                                                            style={{width: '75%'}}
                                                            inputProps={{ min: 1 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            autoHighlight
                                                            options={(() => {
                                                                const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                                return partExpanded?.part?.vendorParts ? partExpanded.part.vendorParts.map(vendorPart => {
                                                                    const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                                                                    return vendor;
                                                                }).filter(vendor => vendor) : [];
                                                            })()}
                                                            getOptionLabel={(option) => option.name || ''}
                                                            value={(() => {
                                                                const partExpanded = this.state.parts.find(x => x.part.id === part.partId);
                                                                const currentCompanyName = this.state.editedCompanyNames[part.id] || part.companyName;
                                                                return partExpanded?.part?.vendorParts ? partExpanded.part.vendorParts.map(vendorPart => {
                                                                    const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                                                                    return vendor;
                                                                }).filter(vendor => vendor).find(vendor => vendor.name === currentCompanyName) : null;
                                                            })()}
                                                            onChange={(event, newValue) => this.handleLineItemVendorChange(part.id, newValue)}
                                                            renderInput={(params) => <TextField {...params} variant="outlined" style={{width: '100%'}} />}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            type="number"
                                                            value={this.state.editedUnitCosts && this.state.editedUnitCosts.hasOwnProperty(part.id) 
                                                                ? this.state.editedUnitCosts[part.id] 
                                                                : this.getUnitCostDisplay(part)}
                                                            onChange={(e) => this.handleLineItemUnitCostChange(part.id, e.target.value)}
                                                            onBlur={(e) => {
                                                                const value = parseFloat(e.target.value) || 0;
                                                                this.setState(prevState => ({
                                                                    editedUnitCosts: {
                                                                        ...prevState.editedUnitCosts,
                                                                        [part.id]: value
                                                                    }
                                                                }));
                                                            }}
                                                            variant="outlined"
                                                            style={{width: '75%'}}
                                                            InputProps={{
                                                                startAdornment: <span style={{marginRight: 8}}>$</span>
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <TextField
                                                            type="number"
                                                            value={this.state.editedTotalPrices && this.state.editedTotalPrices.hasOwnProperty(part.id) 
                                                                ? this.state.editedTotalPrices[part.id] 
                                                                : this.getTotalPriceDisplay(part)}
                                                            onChange={(e) => this.handleLineItemTotalPriceChange(part.id, e.target.value)}
                                                            onBlur={(e) => {
                                                                const value = parseFloat(e.target.value) || 0;
                                                                this.setState(prevState => ({
                                                                    editedTotalPrices: {
                                                                        ...prevState.editedTotalPrices,
                                                                        [part.id]: value
                                                                    }
                                                                }));
                                                            }}
                                                            variant="outlined"
                                                            style={{width: '75%'}}
                                                            InputProps={{
                                                                startAdornment: <span style={{marginRight: 8}}>$</span>
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FontAwesomeIcon
                                                            icon={faTrash}
                                                            style={{ cursor: 'pointer', color: 'black' }}
                                                            onClick={() => this.handleDeleteLineItem(part.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* New line items */}
                                        {this.state.pendingNewLineItems.map((row, index) => {
                                            if (row.isFamilyRow) {
                                                return (
                                                    <tr key={row.id}>
                                                        <td>
                                                            {row.selectedOption ? (
                                                                <div style={{textAlign: 'left'}}>
                                                                    <div style={{fontWeight: 'bold', fontSize: '14px', color: '#333', display: 'flex', alignItems: 'center', gap: '6px'}}>
                                                                        <FontAwesomeIcon icon={faPeopleCarry} style={{ color: '#2196F3' }} />
                                                                        {row.selectedOption.family?.partFamilyId || 'Unknown Family'}
                                                                    </div>
                                                                    <div style={{fontSize: '12px', color: '#555'}}>
                                                                        {row.selectedOption.family?.description || row.description}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <Autocomplete autoHighlight
                                                                    options={this.state.partsOptions || []}
                                                                    getOptionLabel={(option) => {
                                                                        if (option.family) {
                                                                            return option.family.partFamilyId;
                                                                        } else if (option.part) {
                                                                            return `${option.part.partId} rev ${option.part.revision}`;
                                                                        }
                                                                        return option.title || '';
                                                                    }}
                                                                    value={row.selectedOption}
                                                                    onChange={(event, newValue) => this.handleFamilyChange(event, newValue, index)}
                                                                    renderOption={(option) => <PartAutocompleteOption option={option} classes={this.props.classes} />}
                                                                    renderInput={(params) => <TextField {...params} variant="outlined" />}
                                                                />
                                                            )}
                                                        </td>
                                                        <td>
                                                            <TextField
                                                            type="number"
                                                            value={getNumberInputDisplayValue(row.familyQuantity)}
                                                            onChange={(event) => this.handleFamilyQuantityChange(event, index)}
                                                            variant="outlined"
                                                            style={{width: '75%'}}
                                                        />
                                                        </td>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                        <td>
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                style={{ cursor: 'pointer', color: 'black' }}
                                                                onClick={() => this.handleRemovePendingLineItem(index)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            } else {
                                                const partExpanded = this.state.parts.find(x => x.part.id === row.partId);
                                                const selectedVendorPart = row.vendorParts?.find(vp => vp.vendorId === row.vendorId);
                                                
                                                return (
                                                    <tr key={row.id} style={{backgroundColor: row.isFamilyPart ? '#f5f5f5' : '#f0f8ff'}}>
                                                        <td>
                                                            {partExpanded?.part ? (
                                                                <PartDisplay
                                                                    part={partExpanded.part}
                                                                    variant="table"
                                                                    classes={this.props.classes}
                                                                />
                                                            ) : (
                                                                'Unknown'
                                                            )}
                                                        </td>
                                                        <td>
                                                        <TextField
                                                            type="number"
                                                            value={row.partQuantity || row.quantity || ''}
                                                            onChange={(event) => this.handleNewLineItemChange(index, 'quantity', event.target.value)}
                                                            variant="outlined"
                                                            style={{width: '75%'}}
                                                            inputProps={{ min: 1 }}
                                                        />
                                                        </td>
                                                        <td>
                                                            {row.vendorParts && row.vendorParts.length > 0 ? (
                                                                <FormControl variant="outlined" style={{minWidth: 150}}>
                                                                    <Select
                                                                        value={row.vendorId || ''}
                                                                        onChange={(event) => this.handleNewLineItemVendorChange(index, event.target.value)}
                                                                    >
                                                                        {row.vendorParts
                                                                            .filter(vendorPart => {
                                                                                if (this.state.purchaseOrderVendorId) {
                                                                                    return vendorPart.vendorId === this.state.purchaseOrderVendorId;
                                                                                }
                                                                                const vendor = this.state.vendors.find(v => v.id === vendorPart.vendorId);
                                                                                return (vendor?.name || '').trim().toLowerCase() === (this.state.purchaseOrderVendor || '').trim().toLowerCase();
                                                                            })
                                                                            .map((vendorPart) => {
                                                                                const vendor = this.state.vendors.find(x => x.id === vendorPart.vendorId);
                                                                                return (
                                                                                    <MenuItem key={vendorPart.vendorId} value={vendorPart.vendorId}>
                                                                                        {vendor?.name}
                                                                                    </MenuItem>
                                                                                );
                                                                            })
                                                                        }
                                                                    </Select>
                                                                </FormControl>
                                                            ) : (
                                                                this.state.purchaseOrderVendor || ''
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                <TextField
                                                                     type="number"
                                                                     value={row.pricePaid || row.price || 0}
                                                                     onChange={(event) => this.handleNewLineItemChange(index, 'pricePaid', event.target.value)}
                                                                     onBlur={(e) => {
                                                                         const value = parseFloat(e.target.value) || 0;
                                                                         this.handleNewLineItemChange(index, 'pricePaid', value.toString());
                                                                     }}
                                                                     variant="outlined"
                                                                     style={{width: '75%'}}
                                                                     InputProps={{
                                                                         startAdornment: <span style={{marginRight: 8}}>$</span>
                                                                     }}
                                                                 />
                                                                {selectedVendorPart?.costType === 0 && (
                                                                    <FontAwesomeIcon 
                                                                        icon={faInfoCircle} 
                                                                        style={{ cursor: 'help' }}
                                                                        title={this.getTieredPricingTooltip(selectedVendorPart)}
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>${((row.partQuantity || row.quantity) * (row.pricePaid || row.price || 0)).toFixed(3)}</td>
                                                        <td>
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                style={{ cursor: 'pointer', color: 'black' }}
                                                                onClick={() => this.handleRemovePendingLineItem(index)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        })}

                                        {/* Total row */}
                                        <tr style={{backgroundColor: '#f0f0f0', fontWeight: 'bold'}}>
                                            <td colSpan={5} style={{textAlign: 'right'}}>Total:</td>
                                            <td>
                                                ${(() => {
                                                    const existingTotal = editingOrder.purchasedParts
                                                        .filter(part => !(this.state.deletedLineItems || []).includes(part.id))
                                                        .reduce((sum, part) => {
                                                        return sum + this.getTotalPrice(part);
                                                    }, 0);
                                                    
                                                    const newItemsTotal = this.state.pendingNewLineItems
                                                        .filter(row => !row.isFamilyRow)
                                                        .reduce((sum, row) => sum + ((row.partQuantity || row.quantity) * (row.pricePaid || row.price || 0)), 0);
                                                    
                                                    return (existingTotal + newItemsTotal).toFixed(3);
                                                })()}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            <center>
                                <Button onClick={this.handleAddNewLineItem} className="success-button-lg" style={{margin: '20px 0'}}>Add Line</Button>
                                <br/>
                                <div style={{display: 'flex', gap: '15px', justifyContent: 'center'}}>
                                    {this.state.savingPurchaseOrder ? 
                                        <CircularProgress/>
                                        :
                                        <Button
                                            onClick={this.handleSaveLineItems}
                                            className="save-button-lg"
                                            style={{
                                                backgroundColor: '#7fff84',
                                                color: 'black'
                                            }}
                                        >
                                            Save Changes
                                        </Button>
                                    }
                                </div>
                            </center>
                        </DrawerMenuRight>

                        {/* Packing Slip Details Drawer */}
                        <PackingSlipDetails
                            open={this.state.packingSlipDetailsOpen}
                            onClose={this.handleClosePackingSlipDetails}
                            packingSlipId={this.state.selectedPackingSlipId}
                            onUpdate={this.handlePackingSlipDetailsUpdate}
                            parts={this.state.parts}
                            partFamilies={this.state.partFamilies}
                            packingSlips={this.state.editingOrder?.packingSlips}
                        />
                    </div>
                </div>
            </DashboardTemplate>
        );
    }
}

EditPurchaseOrder.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EditPurchaseOrder); 